import React, { useState } from 'react';
import './OverworldEventModal.css';

/**
 * OverworldEventModal — full-screen overlay for overworld random events.
 *
 * Props:
 *   event      — { id, name, description, options[], isTreasureEvent?, rarity?, currentStep?, totalSteps? }
 *   result     — null | { message, goldChange, hpChange, mpChange, items[], forgeTokens }
 *   nextStep   — null | { description, options[], currentStep, totalSteps } (for multi-step treasure events)
 *   character  — player's character data (for gate checks)
 *   onChoose   — (eventId, optionId) => void
 *   onDismiss  — () => void (after reading result)
 *   onContinue — () => void (advance to next step in multi-step events)
 */
const OverworldEventModal = ({ event, result, nextStep, character, onChoose, onDismiss, onContinue }) => {
  const [actionLoading, setActionLoading] = useState(false);

  if (!event) return null;

  const isTreasure = event.isTreasureEvent;
  const rarity = event.rarity || 'common';
  const currentStep = event.currentStep ?? 0;
  const totalSteps = event.totalSteps || 1;

  const rarityLabel = { common: 'Common', rare: 'Rare', legendary: 'Legendary' }[rarity] || 'Common';
  const rarityEmoji = { common: '🗺️', rare: '✨', legendary: '🌟' }[rarity] || '🗺️';

  const isOptionAvailable = (opt) => {
    if (opt.requiresClass) {
      const classes = Array.isArray(opt.requiresClass) ? opt.requiresClass : [opt.requiresClass];
      // If player has an advanced class, match ONLY on advanced_class
      // (prevents base classes like 'Mage' from letting every Mage subclass through)
      const charClasses = character?.advanced_class
        ? [character.advanced_class]
        : [character?.class];
      const normalizedClasses = charClasses.filter(Boolean).map(c => c.toLowerCase());
      if (!classes.some(c => normalizedClasses.includes(c.toLowerCase()))) return false;
    }
    if (opt.requiresAlignment) {
      if (!character?.alignment || !character.alignment.toLowerCase().includes(opt.requiresAlignment.toLowerCase())) return false;
    }
    if (opt.requiresItem) {
      const inv = character?.inventory || [];
      if (!inv.some(i => i && i.name === opt.requiresItem)) return false;
    }
    if (opt.goldCost && (character?.gold || 0) < opt.goldCost) return false;
    return true;
  };

  const handleChoose = async (optionId) => {
    setActionLoading(true);
    await onChoose(event.id, optionId);
    setActionLoading(false);
  };

  // ── RESULT SCREEN ──────────────────────────────────────────────
  if (result) {
    const hasNextStep = !!nextStep;
    const hasRewards = (result.goldChange && result.goldChange !== 0) ||
                       (result.hpChange && result.hpChange !== 0) ||
                       (result.mpChange && result.mpChange !== 0) ||
                       (result.forgeTokens && result.forgeTokens > 0) ||
                       (result.items && result.items.length > 0) ||
                       (result.alignmentShift);

    return (
      <div className="ow-event-overlay">
        <div className={`ow-event-modal ow-event-result-modal ${isTreasure ? `treasure-event-${rarity}` : ''}`}>
          {isTreasure && (
            <div className={`treasure-event-banner treasure-banner-${rarity}`}>
              {rarityEmoji} {rarityLabel} Treasure — Step {currentStep + 1}/{totalSteps}
            </div>
          )}
          <div className="ow-event-icon">⚡</div>
          <h3 className="ow-event-title">{event.name}</h3>
          <p className="ow-event-message">{result.message}</p>

          {hasRewards && (
            <div className="ow-event-rewards">
              {result.goldChange > 0 && (
                <div className="ow-reward gain">💰 +{result.goldChange} gold</div>
              )}
              {result.goldChange < 0 && (
                <div className="ow-reward loss">💰 {result.goldChange} gold</div>
              )}
              {result.hpChange > 0 && result.hpChange < 9999 && (
                <div className="ow-reward gain">❤️ +{result.hpChange} HP restored</div>
              )}
              {result.hpChange === 9999 && (
                <div className="ow-reward gain">❤️ Full HP restored</div>
              )}
              {result.hpChange < 0 && (
                <div className="ow-reward loss">❤️ {result.hpChange} HP</div>
              )}
              {result.mpChange > 0 && result.mpChange < 9999 && (
                <div className="ow-reward gain">💧 +{result.mpChange} MP restored</div>
              )}
              {result.mpChange === 9999 && (
                <div className="ow-reward gain">💧 Full MP restored</div>
              )}
              {result.mpChange < 0 && (
                <div className="ow-reward loss">💧 {result.mpChange} MP</div>
              )}
              {result.forgeTokens > 0 && (
                <div className="ow-reward gain">🔧 +{result.forgeTokens} Forge Token{result.forgeTokens > 1 ? 's' : ''}</div>
              )}
              {result.items && result.items.map((item, i) => (
                <div key={i} className="ow-reward gain">📦 {item.name}{item.qty > 1 ? ` x${item.qty}` : ''}</div>
              ))}
              {result.alignmentShift && (
                <div className="ow-reward alignment-shift">⚖️ Alignment: {result.alignmentShift.fromLabel} → {result.alignmentShift.toLabel}</div>
              )}
            </div>
          )}

          {hasNextStep ? (
            <button className="ow-event-btn ow-event-btn-continue treasure-btn-next" onClick={onContinue}>
              Continue the adventure...
            </button>
          ) : (
            <button className="ow-event-btn ow-event-btn-continue" onClick={onDismiss}>
              Continue
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── CHOICES SCREEN ─────────────────────────────────────────────
  return (
    <div className="ow-event-overlay">
      <div className={`ow-event-modal ${isTreasure ? `treasure-event-${rarity}` : ''}`}>
        {isTreasure && (
          <div className={`treasure-event-banner treasure-banner-${rarity}`}>
            {rarityEmoji} {rarityLabel} Treasure — Step {currentStep + 1}/{totalSteps}
          </div>
        )}
        <div className="ow-event-icon">{isTreasure ? rarityEmoji : '📜'}</div>
        <h3 className="ow-event-title">{event.name}</h3>
        <p className="ow-event-description">{event.description}</p>

        <div className="ow-event-options">
          {event.options.map(opt => {
            const available = isOptionAvailable(opt);
            return (
              <button
                key={opt.id}
                className={`ow-event-option ${available ? '' : 'ow-option-locked'}`}
                disabled={!available || actionLoading}
                onClick={() => available && handleChoose(opt.id)}
                title={!available ? 'Requirements not met' : opt.text}
              >
                <span className="ow-option-text">{opt.text}</span>
                <span className="ow-option-tags">
                  {opt.requiresClass && (
                    <span className="ow-tag ow-tag-class">
                      {Array.isArray(opt.requiresClass) ? opt.requiresClass.slice(0, 3).join('/') : opt.requiresClass}
                    </span>
                  )}
                  {opt.requiresAlignment && (
                    <span className="ow-tag ow-tag-align">{opt.requiresAlignment}</span>
                  )}
                  {opt.requiresItem && (
                    <span className="ow-tag ow-tag-item">needs {opt.requiresItem}</span>
                  )}
                  {opt.goldCost && (character?.gold || 0) < opt.goldCost && (
                    <span className="ow-tag ow-tag-gold">need {opt.goldCost}g</span>
                  )}
                  {!available && <span className="ow-option-lock">🔒</span>}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default OverworldEventModal;
